/**
 * @fileoverview FlawSection Component - Clean, Compact Design
 * @module FlawSection
 * 
 * Interactive activity component for each flaw type.
 * Redesigned for better information density and scannability.
 * Used in Step 3: Find Flaws in County's Evidence.
 */

import React from 'react';
import FlawChecklist, { ChecklistItem } from './FlawChecklist';
import ClassificationActivity, { 
  TextboxConfig, 
  ClassificationResource, 
  TextboxFlawData 
} from './ClassificationActivity';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Building2,
  Star,
  ThermometerSun,
  Calendar
} from 'lucide-react';

export type FlawType = 
  | 'WRONG_DETAILS' 
  | 'QUALITY_MISCLASSIFICATION' 
  | 'CONDITION_MISCLASSIFICATION' 
  | 'OUTDATED_SALES';

export type FlawActivityType = 'checklist' | 'textbox';

export interface FlawSectionProps {
  flawType: FlawType;
  flawNumber: number;
  title: string;
  shortTitle: string;
  description: string;
  whyItMatters: string;
  checklistItems: ChecklistItem[];
  initialChecklistData?: Record<string, boolean | string>;
  initialFollowUpData?: Record<string, Record<string, string | number>>;
  initialNotes?: string;
  onSave: (data: { 
    checklistData: Record<string, boolean | string> | TextboxFlawData; 
    followUpData: Record<string, Record<string, string | number>>;
    notes: string;
  }) => Promise<void>;
  isExpanded?: boolean;
  onToggle?: () => void;
  isSaving?: boolean;
  hideVideo?: boolean;
  activityType?: FlawActivityType | undefined;
  textboxConfig?: TextboxConfig | undefined;
  classificationResources?: ClassificationResource[] | undefined;
  /** Number of items pre-populated from Step 2 maintenance data (for Condition flaw) */
  prePopulatedItemCount?: number;
}

const FLAW_ICONS: Record<FlawType, React.ReactNode> = {
  WRONG_DETAILS: <Building2 className="w-4 h-4" />,
  QUALITY_MISCLASSIFICATION: <Star className="w-4 h-4" />,
  CONDITION_MISCLASSIFICATION: <ThermometerSun className="w-4 h-4" />,
  OUTDATED_SALES: <Calendar className="w-4 h-4" />
};

const FLAW_COLORS: Record<FlawType, { bg: string; text: string; border: string; badge: string }> = {
  WRONG_DETAILS: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', badge: 'bg-violet-100' },
  QUALITY_MISCLASSIFICATION: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', badge: 'bg-blue-100' },
  CONDITION_MISCLASSIFICATION: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-300', badge: 'bg-slate-100' },
  OUTDATED_SALES: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200', badge: 'bg-sky-100' }
};

const FlawSection: React.FC<FlawSectionProps> = ({
  flawType,
  flawNumber,
  title,
  shortTitle,
  description,
  whyItMatters,
  checklistItems,
  initialChecklistData = {},
  initialFollowUpData = {},
  initialNotes = '',
  onSave,
  isExpanded = false,
  onToggle,
  isSaving = false,
  hideVideo = false,
  activityType = 'checklist',
  textboxConfig,
  classificationResources = [],
  prePopulatedItemCount = 0
}) => {
  const colors = FLAW_COLORS[flawType];
  
  // Calculate findings based on activity type
  let hasFindings = false;
  let findingsDisplay = '';
  
  if (activityType === 'textbox') {
    const textboxData = initialChecklistData as unknown as Partial<TextboxFlawData>;
    const hasRating = typeof textboxData?.countyRating === 'string' && textboxData.countyRating.trim().length > 0;
    const hasItems = Array.isArray(textboxData?.items) && textboxData.items.length > 0;
    const hasLegacyText = typeof textboxData?.misclassifications === 'string' && textboxData.misclassifications.trim().length > 0;
    hasFindings = hasRating || hasItems || hasLegacyText;
    const itemCount = hasItems ? textboxData.items!.length : 0;
    findingsDisplay = hasFindings ? (itemCount > 0 ? `${itemCount} found` : 'Saved') : '';
  } else {
    const checkedCount = Object.values(initialChecklistData).filter(v => v === true).length;
    hasFindings = checkedCount > 0;
    findingsDisplay = hasFindings ? `${checkedCount} found` : '';
  }

  // Inline version (when hideVideo is true)
  if (hideVideo) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
        hasFindings ? colors.border : 'border-gray-200'
      }`}>
        {/* Compact Header */}
        <div className={`px-4 py-3 border-b ${hasFindings ? colors.bg : 'bg-gray-50'} border-gray-100`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.badge} ${colors.text}`}>
                {FLAW_ICONS[flawType]}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
                    Flaw {flawNumber}
                  </span>
                  {hasFindings && (
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${colors.badge} ${colors.text} rounded text-xs font-medium`}>
                      <CheckCircle2 className="w-3 h-3" />
                      {findingsDisplay}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{shortTitle}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Why It Matters - Collapsed tooltip style */}
        <details className="group">
          <summary className="px-4 py-2 bg-slate-50/50 border-b border-slate-100 cursor-pointer text-xs text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1">
            <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
            <span className="font-medium">Why this matters</span>
          </summary>
          <div className="px-4 py-2 bg-slate-50/30 border-b border-slate-100/50">
            <p className="text-xs text-slate-700">{whyItMatters}</p>
          </div>
        </details>

        {/* Activity Content */}
        <div className="p-4">
          {activityType === 'textbox' && textboxConfig ? (
            <ClassificationActivity
              flawType={flawType}
              config={textboxConfig}
              resources={classificationResources}
              initialData={initialChecklistData as unknown as TextboxFlawData}
              initialNotes={initialNotes}
              onSave={onSave as (data: { checklistData: TextboxFlawData; followUpData: Record<string, Record<string, string | number>>; notes: string; }) => Promise<void>}
              isSaving={isSaving}
              prePopulatedItemCount={prePopulatedItemCount}
            />
          ) : (
            <FlawChecklist
              flawType={flawType}
              items={checklistItems}
              initialData={initialChecklistData as Record<string, boolean>}
              initialFollowUpData={initialFollowUpData}
              initialNotes={initialNotes}
              onSave={onSave as (data: { checklistData: Record<string, boolean>; followUpData: Record<string, Record<string, string | number>>; notes: string; }) => Promise<void>}
              isSaving={isSaving}
            />
          )}
        </div>
      </div>
    );
  }

  // Collapsible version
  return (
    <div className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all ${
      hasFindings ? colors.border : 'border-gray-200'
    }`}>
      {/* Collapsed Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            hasFindings ? `${colors.badge} ${colors.text}` : 'bg-gray-100 text-gray-500'
          }`}>
            {FLAW_ICONS[flawType]}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold uppercase tracking-wide ${colors.text}`}>
                Flaw {flawNumber}
              </span>
              {hasFindings && (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${colors.badge} ${colors.text} rounded text-xs font-medium`}>
                  <CheckCircle2 className="w-3 h-3" />
                  {findingsDisplay}
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900">{shortTitle}</h3>
          </div>
        </div>
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Description */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs text-gray-600">{description}</p>
          </div>

          {/* Why It Matters */}
          <details className="group">
            <summary className="px-4 py-2 bg-slate-50/50 border-b border-slate-100 cursor-pointer text-xs text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1">
              <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
              <span className="font-medium">Why this matters</span>
            </summary>
            <div className="px-4 py-2 bg-slate-50/30 border-b border-slate-100/50">
              <p className="text-xs text-slate-700">{whyItMatters}</p>
            </div>
          </details>

          {/* Activity */}
          <div className="p-4">
            {activityType === 'textbox' && textboxConfig ? (
              <ClassificationActivity
                flawType={flawType}
                config={textboxConfig}
                resources={classificationResources}
                initialData={initialChecklistData as unknown as TextboxFlawData}
                initialNotes={initialNotes}
                onSave={onSave as (data: { checklistData: TextboxFlawData; followUpData: Record<string, Record<string, string | number>>; notes: string; }) => Promise<void>}
                isSaving={isSaving}
                prePopulatedItemCount={prePopulatedItemCount}
              />
            ) : (
              <FlawChecklist
                flawType={flawType}
                items={checklistItems}
                initialData={initialChecklistData as Record<string, boolean>}
                initialFollowUpData={initialFollowUpData}
                initialNotes={initialNotes}
                onSave={onSave as (data: { checklistData: Record<string, boolean>; followUpData: Record<string, Record<string, string | number>>; notes: string; }) => Promise<void>}
                isSaving={isSaving}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlawSection;
